import {
  Component,
  inject,
  output,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  UIPopoverDirective,
  UIPopoverRef,
  UIPopoverTargetDirective,
  UIPopoverTemplateDirective
} from '@bannerflow/ui';
import { POPOVER_CONFIG } from '../../../constants';
import { NotificationClickEvent } from '../../../notification.types';
import { NotificationService } from '../../../services/notification.service';
import { NotificationItemComponent } from '../notification-item/notification-item.component';

const INITIAL_DELAY = 3000;
const MOUSELEAVE_DELAY = 800;

@Component({
  imports: [
    NotificationItemComponent,
    UIPopoverDirective,
    UIPopoverTemplateDirective
  ],
  selector: 'ns-quick-popover',
  template: `
    <ui-popover
      #popover="ui-popover"
      [config]="popoverConfig">
      <ng-template ui-popover-template>
        <div
          class="wrapper"
          (mouseenter)="mouseEntered()"
          (mouseleave)="mouseLeft()">
          @let _notification = notification();
          @if (_notification) {
            <ns-notification-item
              [notification]="_notification"
              (notificationClicked)="handleNotificationClicked($event)" />
          }
        </div>
      </ng-template>
    </ui-popover>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .wrapper {
      overflow-x: hidden;
    }
  `
})
export class QuickPopoverComponent {
  private notificationsService = inject(NotificationService);

  notification = toSignal(this.notificationsService.newNotification$);
  notificationClicked = output<NotificationClickEvent>();

  private popover = viewChild.required<UIPopoverDirective>('popover');
  private beingHovered = false;
  private popoverRef: UIPopoverRef | undefined;

  protected popoverConfig = POPOVER_CONFIG;

  openPopover(target: UIPopoverTargetDirective): UIPopoverRef {
    const popover = this.popover();
    if (!popover) {
      throw new Error('Could not find popover child');
    }
    this.popoverRef = popover.open(target);

    setTimeout(() => {
      this.closePopover();
    }, INITIAL_DELAY);
    return this.popoverRef;
  }

  handleNotificationClicked(event: NotificationClickEvent): void {
    this.notificationClicked.emit(event);
  }

  mouseEntered(): void {
    this.beingHovered = true;
  }

  mouseLeft(): void {
    this.beingHovered = false;
    setTimeout(() => {
      this.closePopover();
    }, MOUSELEAVE_DELAY);
  }

  private closePopover(): void {
    if (!this.popoverRef || this.beingHovered) {
      return;
    }

    this.popoverRef.close();
  }
}
